/**
 * Pages this device has visited in a web pane, for the new-pane box to suggest.
 *
 * ON THIS DEVICE, in localStorage, the way a browser keeps history. Not in the
 * World: that document is read by everyone in the project, and what you
 * browsed is not theirs to read. The cost is that history does not follow you
 * to your other machine.
 *
 * Only the desktop client ever writes here, because only it embeds pages. The
 * web client opens a url as a browser tab and sees nothing after that.
 */

const KEY = 'mod:web-history'
const CAP = 500

export interface WebVisit {
  url: string
  title: string
  visits: number
  lastVisited: number
}

export function readWebHistory(): WebVisit[] {
  try {
    const raw = localStorage.getItem(KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

/** Record a visit. Pure over `history` so the ordering is testable. */
export function withVisit(
  history: WebVisit[],
  url: string,
  title: string,
  now: number,
): WebVisit[] {
  const prior = history.find((v) => v.url === url)
  const visit: WebVisit = {
    url,
    // A page still loading reports its url as its title; keep a real one.
    title: title && title !== url ? title : (prior?.title ?? ''),
    visits: (prior?.visits ?? 0) + 1,
    lastVisited: now,
  }
  return [visit, ...history.filter((v) => v.url !== url)].slice(0, CAP)
}

export function recordWebVisit(url: string, title: string): void {
  if (!/^https?:\/\//i.test(url)) return
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify(withVisit(readWebHistory(), url, title, Date.now())),
    )
  } catch {
    // best-effort: a browser refusing storage costs a suggestion, nothing more.
  }
}

/** The url as a person types it: no scheme, no `www.`. */
const bare = (url: string) =>
  url.replace(/^https?:\/\//i, '').replace(/^www\./i, '')

/**
 * Visits matching what was typed, best first.
 *
 * A url that STARTS with the text beats one that merely contains it, since
 * typing "git" means github.com far more often than a page with git in its
 * path. Within that, pages you return to beat pages you saw once.
 */
export function matchWebHistory(
  history: WebVisit[],
  query: string,
  limit = 5,
): WebVisit[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  const scored: { v: WebVisit; score: number }[] = []
  for (const v of history) {
    const u = bare(v.url).toLowerCase()
    const t = v.title.toLowerCase()
    const score = u.startsWith(q)
      ? 3
      : u.includes(q)
        ? 2
        : t.includes(q)
          ? 1
          : 0
    if (score) scored.push({ v, score })
  }
  return scored
    .sort(
      (a, b) =>
        b.score - a.score ||
        b.v.visits - a.v.visits ||
        b.v.lastVisited - a.v.lastVisited,
    )
    .slice(0, limit)
    .map((s) => s.v)
}
